import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { Action, Panel, PanelHeader, StatusBadge } from "./ui";
import { ConversationModal, LEVEL_TONE, ScheduleModal, formatDay } from "./Conversations";

// Agenda operacional do conselheiro logado (Hoje / Próximas), sempre dentro da ala autorizada.
export const AgendaPanel = ({ ward, counselorId }) => {
  const { api } = useAuth();
  const [scope, setScope] = useState("today");
  const [items, setItems] = useState(null);
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/portal/conversations/agenda", {
        params: { scope, ward, counselor_id: counselorId || undefined },
      });
      setItems(data.items);
    } catch {
      setItems([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, ward, counselorId]);

  useEffect(() => {
    load();
  }, [load]);

  const cancel = async (id) => {
    try {
      await api.post(`/portal/conversations/schedules/${id}/cancel`);
      toast.success("Agendamento cancelado.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <Panel testId="agenda-panel" className="space-y-4">
      <PanelHeader
        label="Conversas"
        title={counselorId === undefined ? "Minha Agenda de Conversas" : "Agenda de Conversas"}
        hint={
          counselorId === undefined
            ? "Compromissos com os pacientes sob sua responsabilidade."
            : "Compromissos agendados dentro da ala autorizada."
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {[
              { key: "today", label: "Hoje" },
              { key: "upcoming", label: "Próximas" },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                data-testid={`agenda-tab-${tab.key}`}
                onClick={() => setScope(tab.key)}
                className={`rounded-full px-3.5 py-1.5 text-[13.5px] font-semibold ${
                  scope === tab.key ? "bg-sage-600 text-white" : "bg-sand text-ink-700"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        }
      />
      <div data-testid="agenda-items" className="space-y-2.5">
        {items === null ? (
          <p className="text-[14px] text-ink-500">Carregando…</p>
        ) : items.length === 0 ? (
          <p data-testid="agenda-empty" className="text-[14px] text-ink-500">
            {scope === "today" ? "Nenhuma conversa agendada para hoje." : "Nenhuma conversa nos próximos dias."}
          </p>
        ) : (
          items.map((item) => (
            <div
              key={item.id}
              data-testid={`agenda-item-${item.id}`}
              className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-white p-3.5"
            >
              <div className="min-w-0">
                <p className="text-[15px] font-semibold text-ink-900">
                  {scope === "today" ? item.scheduled_time : `${formatDay(item.scheduled_date)} · ${item.scheduled_time}`} —{" "}
                  {item.patient_name}
                </p>
                <p className="text-[13px] text-ink-500">
                  {item.duration_minutes} minutos · {item.counselor_name}
                  {item.reminder ? ` · ${item.reminder}` : ""}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Link
                  to={`/portal/pacientes/${item.patient_id}`}
                  data-testid={`agenda-open-patient-${item.id}`}
                  className="inline-flex min-h-[38px] items-center rounded-full px-4 text-[13.5px] font-semibold text-ink-600 hover:bg-sand"
                >
                  Abrir paciente
                </Link>
                <Action
                  size="sm"
                  icon={MessageSquarePlus}
                  data-testid={`agenda-register-${item.id}`}
                  onClick={() => setModal({ type: "conversation", item })}
                >
                  Registrar conversa
                </Action>
                <Action
                  size="sm"
                  variant="secondary"
                  icon={CalendarClock}
                  data-testid={`agenda-reschedule-${item.id}`}
                  onClick={() => setModal({ type: "reschedule", item })}
                >
                  Reagendar
                </Action>
                <Action size="sm" variant="ghost" data-testid={`agenda-cancel-${item.id}`} onClick={() => cancel(item.id)}>
                  Cancelar
                </Action>
              </div>
            </div>
          ))
        )}
      </div>

      {modal?.type === "conversation" ? (
        <ConversationModal
          patient={{ id: modal.item.patient_id, full_name: modal.item.patient_name, counselor_id: modal.item.counselor_id }}
          schedule={modal.item}
          onClose={() => setModal(null)}
          onSaved={load}
        />
      ) : null}
      {modal?.type === "reschedule" ? (
        <ScheduleModal
          patient={{ id: modal.item.patient_id, full_name: modal.item.patient_name }}
          schedule={modal.item}
          mode="reschedule"
          onClose={() => setModal(null)}
          onSaved={load}
        />
      ) : null}
    </Panel>
  );
};

// Pacientes ordenados pelos que estão há mais tempo sem conversa realizada.
export const AttentionPanel = ({ ward, counselorId }) => {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: payload } = await api.get("/portal/conversations/attention", {
        params: { ward, counselor_id: counselorId || undefined },
      });
      setData(payload);
    } catch {
      setData({ items: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ward, counselorId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Panel testId="attention-panel" className="space-y-4">
      <PanelHeader
        label="Conversas"
        title="Pacientes que precisam de atenção"
        hint={
          data
            ? `${data.alert_count} paciente(s) há ${data.alert_days} dias ou mais sem conversa registrada.`
            : "Carregando…"
        }
      />
      <div data-testid="attention-items" className="space-y-2.5">
        {(data?.items || []).length === 0 ? (
          <p data-testid="attention-empty" className="text-[14px] text-ink-500">
            {counselorId === undefined
              ? "Nenhum paciente ativo sob sua responsabilidade."
              : "Nenhum paciente ativo para este filtro."}
          </p>
        ) : (
          data.items.map((row) => (
            <div
              key={row.patient_id}
              data-testid={`attention-item-${row.patient_id}`}
              className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-white p-3.5"
            >
              <div className="min-w-0">
                <p className="text-[15px] font-semibold text-ink-900">{row.patient_name}</p>
                <p className="text-[13.5px] text-ink-600">{row.label}</p>
                <p className="text-[13px] text-ink-500">
                  Última conversa: {formatDay(row.last_conversation_date)}
                  {row.last_conversation_counselor ? ` · ${row.last_conversation_counselor}` : ""}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge tone={LEVEL_TONE[row.level]} testId={`attention-level-${row.patient_id}`}>
                  {row.level_label}
                </StatusBadge>
                <Action
                  size="sm"
                  icon={MessageSquarePlus}
                  data-testid={`attention-register-${row.patient_id}`}
                  onClick={() => setModal(row)}
                >
                  Registrar conversa
                </Action>
              </div>
            </div>
          ))
        )}
      </div>

      {modal ? (
        <ConversationModal
          patient={{ id: modal.patient_id, full_name: modal.patient_name, counselor_id: modal.counselor_id }}
          onClose={() => setModal(null)}
          onSaved={load}
        />
      ) : null}
    </Panel>
  );
};
